import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SLOTS = [
  { key: 'open', label: 'Market Open', time: '9:15 AM IST', emoji: '🔔' },
  { key: 'mid_morning', label: 'Mid-Morning Pulse', time: '11:15 AM IST', emoji: '☀️' },
  { key: 'afternoon', label: 'Afternoon Pulse', time: '1:30 PM IST', emoji: '🌤️' },
  { key: 'close', label: 'Market Close', time: '3:30 PM IST', emoji: '🏁' },
];

const MarketUpdatesTab = () => {
  const [previews, setPreviews] = useState({});  // {slot: text}
  const [loadingSlot, setLoadingSlot] = useState('');
  const [broadcasting, setBroadcasting] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [autoEnabled, setAutoEnabled] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    loadHistory();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/settings`);
      const j = await res.json();
      setAutoEnabled(!!j.enabled);
    } catch (_e) { /* ignore */ }
  };

  const toggleAuto = async () => {
    const next = !autoEnabled;
    setSavingToggle(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/settings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      const j = await res.json();
      setAutoEnabled(!!j.enabled);
      toast.success(next ? 'Automatic market updates turned ON' : 'Automatic market updates turned OFF');
    } catch (_e) {
      toast.error('Could not update setting');
    } finally {
      setSavingToggle(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/history?limit=20`);
      const j = await res.json();
      setHistory(j.updates || []);
    } catch (_e) {
      // ignore
    } finally {
      setLoadingHistory(false);
    }
  };

  const previewSlot = async (slot) => {
    setLoadingSlot(slot);
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/preview?slot=${slot}`);
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Preview failed');
      setPreviews((p) => ({ ...p, [slot]: j.text }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingSlot('');
    }
  };

  const broadcastSlot = async (slot) => {
    if (!window.confirm(`Send "${SLOTS.find((s) => s.key === slot)?.label}" to the public Telegram channel now?`)) return;
    setBroadcasting(slot);
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/broadcast?slot=${slot}`, {
        method: 'POST',
        cache: 'no-store',
      });
      const rawBody = await res.text();
      let j;
      try { j = JSON.parse(rawBody); } catch (_e) { j = { _rawBody: rawBody }; }

      if (!res.ok) {
        toast.error(`HTTP ${res.status} — ${j.detail || j._rawBody || 'Broadcast failed'}`, { duration: 10000 });
        console.error('broadcastSlot failed:', res.status, j);
        return;
      }
      // New API returns 'accepted' status — broadcast runs in background
      if (j.status === 'accepted') {
        toast.success(`📨 Broadcast queued — message will arrive in 30-60s. Check Recent Broadcasts to confirm.`, { duration: 8000 });
        // Auto-refresh history a few times to catch the result
        setTimeout(loadHistory, 5000);
        setTimeout(loadHistory, 20000);
        setTimeout(loadHistory, 45000);
      } else if (j?.result?.ok) {
        toast.success(`✓ Broadcast sent to Telegram`);
        await loadHistory();
      } else {
        const reason = j?.result?.reason || j?.result?.body || j?.result?.error || JSON.stringify(j?.result || j);
        toast.error(`Telegram rejected: ${reason}`, { duration: 12000 });
      }
    } catch (e) {
      toast.error(`Network error: ${e.message || e}`, { duration: 10000 });
      console.error('broadcastSlot network error:', e);
    } finally {
      setBroadcasting('');
    }
  };

  const flashSend = async () => {
    if (!window.confirm('Send a Flash Update to the public Telegram channel right now?')) return;
    setBroadcasting('flash');
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/broadcast?slot=flash`, {
        method: 'POST',
        cache: 'no-store',
      });
      const rawBody = await res.text();
      let j;
      try { j = JSON.parse(rawBody); } catch (_e) { j = { _rawBody: rawBody }; }

      if (!res.ok) {
        toast.error(`HTTP ${res.status} — ${j.detail || j._rawBody || 'Broadcast failed'}`, { duration: 10000 });
        return;
      }
      if (j.status === 'accepted') {
        toast.success('⚡ Flash broadcast queued — message arriving in 30-60s', { duration: 8000 });
        setTimeout(loadHistory, 5000);
        setTimeout(loadHistory, 20000);
        setTimeout(loadHistory, 45000);
      } else if (j?.result?.ok) {
        toast.success('⚡ Flash broadcast sent ✓');
        await loadHistory();
      } else {
        const reason = j?.result?.reason || j?.result?.body || j?.result?.error || JSON.stringify(j?.result || j);
        toast.error(`Telegram rejected: ${reason}`, { duration: 12000 });
      }
    } catch (e) {
      toast.error(`Network error: ${e.message || e}`, { duration: 10000 });
    } finally {
      setBroadcasting('');
    }
  };

  const flashPreview = async () => {
    setLoadingSlot('flash');
    try {
      const res = await fetch(`${API_URL}/api/admin/market-updates/preview?slot=flash`);
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Preview failed');
      setPreviews((p) => ({ ...p, flash: j.text }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingSlot('');
    }
  };

  return (
    <div className="space-y-6" data-testid="market-updates-tab">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">📢 Intraday Market Updates</h2>
        <p className="text-sm text-gray-600 mt-1">
          4× daily Telegram broadcasts (Mon-Fri). Each slot auto-fires at its IST time. You can preview or manually fire any slot below.
        </p>
      </div>

      {/* MASTER ON/OFF for the 4x/day automatic broadcasts */}
      <Card className={autoEnabled ? 'border-2 border-green-300 bg-green-50' : 'border-2 border-gray-300 bg-gray-50'} data-testid="auto-broadcast-toggle-card">
        <CardContent className="p-5 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              Automatic Daily Broadcasts
              <Badge className={autoEnabled ? 'bg-green-600' : 'bg-gray-500'}>{autoEnabled === null ? '…' : (autoEnabled ? 'ON' : 'OFF')}</Badge>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              When OFF, the scheduled 9:15 / 11:15 / 1:30 / 3:30 messages will <b>not</b> be sent to your Telegram channel.
              Manual “Broadcast Now” below still works anytime.
            </p>
          </div>
          <Button
            onClick={toggleAuto}
            disabled={savingToggle || autoEnabled === null}
            className={autoEnabled ? 'bg-red-600 hover:bg-red-700 text-white whitespace-nowrap' : 'bg-green-600 hover:bg-green-700 text-white whitespace-nowrap'}
            data-testid="auto-broadcast-toggle-btn"
          >
            {savingToggle ? 'Saving…' : (autoEnabled ? 'Turn OFF' : 'Turn ON')}
          </Button>
        </CardContent>
      </Card>

      {/* FLASH UPDATE — hero card */}
      <Card className="border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-amber-100" data-testid="flash-update-card">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
              ⚡ Flash Update
            </h3>
            <p className="text-sm text-amber-800/80 mt-1">
              Spot a sudden move? Push a rich market update to your Telegram channel right now with the latest data + current timestamp.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={flashPreview}
              disabled={loadingSlot === 'flash'}
              className="border-amber-400 text-amber-800 hover:bg-amber-200"
              data-testid="flash-preview-btn"
            >
              {loadingSlot === 'flash' ? 'Loading...' : 'Preview'}
            </Button>
            <Button
              onClick={flashSend}
              disabled={broadcasting === 'flash'}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold shadow-md"
              data-testid="flash-send-btn"
            >
              {broadcasting === 'flash' ? 'Sending…' : '⚡ Push Update Now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {previews.flash && (
        <pre
          className="text-xs bg-amber-50 border border-amber-200 rounded p-3 whitespace-pre-wrap text-amber-900 max-h-72 overflow-y-auto"
          data-testid="flash-preview-text"
        >
          {previews.flash}
        </pre>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SLOTS.map((s) => (
          <Card key={s.key} className="border-amber-200" data-testid={`slot-card-${s.key}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="text-xl">{s.emoji}</span> {s.label}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">Auto-fires: {s.time}</p>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                  {s.key}
                </Badge>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => previewSlot(s.key)}
                  disabled={loadingSlot === s.key}
                  data-testid={`preview-${s.key}-btn`}
                >
                  {loadingSlot === s.key ? 'Loading...' : 'Preview'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => broadcastSlot(s.key)}
                  disabled={broadcasting === s.key}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid={`broadcast-${s.key}-btn`}
                >
                  {broadcasting === s.key ? 'Sending...' : 'Send now'}
                </Button>
              </div>

              {previews[s.key] && (
                <pre
                  className="text-xs bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-gray-700 max-h-72 overflow-y-auto"
                  data-testid={`preview-${s.key}-text`}
                >
                  {previews[s.key]}
                </pre>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Broadcasts</h3>
        {loadingHistory ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">No broadcasts yet. They'll appear here after the first auto/manual send.</p>
        ) : (
          <div className="space-y-2" data-testid="market-updates-history">
            {history.map((h) => (
              <div key={`${h.date}_${h.slot}`} className="p-3 bg-white border border-gray-200 rounded-lg text-sm flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">
                    {SLOTS.find((s) => s.key === h.slot)?.emoji} {h.label || h.slot}
                  </span>
                  <span className="text-gray-500 text-xs ml-2">
                    {h.date} · {new Date(h.broadcasted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <Badge className={(h.result?.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700') + ' text-[10px]'}>
                  {h.result?.ok ? 'sent' : 'failed'} · {h.trigger || 'manual'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketUpdatesTab;
